<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button class="btn btn-primary" @click="handleGoToBatchIssue">
                <i class="ri-gift-line"></i>
                批次發放點數
              </button>
      </div>
    </div>

    <div class="page-container">
    <div v-if="isWixtarClient" class="alert alert-info wixtar-banner" role="status">
      <strong>Wixtar 客戶：</strong>列表為 CRM 發放紀錄；實際入點以 Wixtar 為準。明細頁「點數交易」關聯可能僅含訂單冪等標記，完整流水請用點數交易查詢（鏡像）或 Wixtar 後台。
    </div>

    <div class="page-toolbar">
      <div class="search-box">
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          placeholder="搜尋批次 ID 或發放原因..."
          @input="handleSearch"
        />
        <i class="ri-search-line"></i>
      </div>
      <div class="filter-group">
        <select v-model="filterStatus" class="form-control" @change="handleFilterChange">
          <option value="">全部狀態</option>
          <option value="processing">執行中</option>
          <option value="completed">已完成</option>
          <option value="failed">失敗</option>
          <option value="cancelled">已取消</option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchBatchIssuances">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>批次 ID</th>
            <th>發放原因</th>
            <th>點數</th>
            <th>狀態</th>
            <th>會員數</th>
            <th>成功/失敗</th>
            <th>進度</th>
            <th>建立時間</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="batchIssuances.length === 0">
            <td colspan="9" class="empty-state">
              沒有找到批次發放點數記錄
            </td>
          </tr>
          <tr v-for="issuance in batchIssuances" :key="issuance.id">
            <td>
              <code class="code-badge">{{ issuance.batchId || issuance.id }}</code>
            </td>
            <td>{{ issuance.reason || '-' }}</td>
            <td>{{ issuance.points || 0 }}</td>
            <td>
              <span :class="getStatusBadgeClass(issuance.status)">
                {{ getStatusText(issuance.status) }}
              </span>
            </td>
            <td>{{ issuance.totalMembers || 0 }}</td>
            <td>
              <span v-if="issuance.status === 'completed'">
                {{ issuance.successCount || 0 }} / {{ issuance.failedCount || 0 }}
              </span>
              <span v-else>-</span>
            </td>
            <td>
              <div v-if="issuance.status === 'processing'" class="progress-bar">
                <div class="progress-fill" :style="{ width: `${issuance.progressPercentage || 0}%` }"></div>
                <span class="progress-text">{{ issuance.progressPercentage || 0 }}%</span>
              </div>
              <span v-else>-</span>
            </td>
            <td>{{ formatDateTime(issuance.createdAt) }}</td>
            <td>
              <div class="action-buttons">
                <button class="btn btn-sm btn-secondary" @click="handleView(issuance.batchId || issuance.id)">
                  <i class="ri-eye-line"></i>
                  查看
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- 分頁 -->
      <ListPagination
        v-if="pagination && pagination.totalPages > 1"
        :page="pagination.page"
        :total-pages="pagination.totalPages"
        :total="pagination.totalItems"
        @change="changePage"
      />
    </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { pointsService } from '@/api/modules/points.js';
import { clientService } from '@/api/modules/clients.js';
import authService from '@/helpers/authservice/authService.js';
import { showError } from '@/utils/swal.js';

export default {
  name: 'PointsBatchIssuances',
  setup() {
    const router = useRouter();
    const { pageTitle } = usePageTitle('批次發放點數');
    const batchIssuances = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const pagination = ref(null);
    const searchQuery = ref('');
    const filterStatus = ref('');
    const searchTimeout = ref(null);
    const isWixtarClient = ref(false);

    const loadClientMemberSystem = async () => {
      const user = authService.getCurrentUser();
      if (!user?.clientId) return;
      try {
        const res = await clientService.getClient(user.clientId);
        if (res?.success && res.data?.clientData?.memberSystemType) {
          isWixtarClient.value = res.data.clientData.memberSystemType === 'WIXTAR';
        }
      } catch (e) {
        console.error(e);
      }
    };

    const fetchBatchIssuances = async () => {
      loading.value = true;
      error.value = null;

      try {
        const params = {
          page: pagination.value?.page || 1,
          pageSize: 20
        };

        if (searchQuery.value) {
          params.search = searchQuery.value;
        }
        if (filterStatus.value) {
          params.status = filterStatus.value;
        }

        const response = await pointsService.getBatchIssuances(params);

        if (response && response.success && response.data) {
          // 轉換資料格式以符合前端顯示需求
          batchIssuances.value = (response.data.items || []).map(item => ({
            id: item.id,
            batchId: item.issuanceId || item.id,
            reason: item.campaignName || '-',
            points: item.pointsAmount || 0,
            status: item.status,
            totalMembers: item.targetMemberCount || 0,
            successCount: item.successCount || 0,
            failedCount: item.failureCount || 0,
            progressPercentage: item.status === 'processing' 
              ? Math.round(((item.successCount || 0) + (item.failureCount || 0)) / (item.targetMemberCount || 1) * 100)
              : item.status === 'completed' ? 100 : 0,
            createdAt: item.createdAt
          }));
          pagination.value = response.data.pagination;
        } else {
          error.value = response?.message || '獲取批次發放點數列表失敗';
        }
      } catch (err) {
        console.error('獲取批次發放點數列表失敗:', err);
        
        // 檢查是否是代理連接錯誤
        if (err.message && err.message.includes('ECONNREFUSED')) {
          error.value = '無法連接到後端服務器，請確認後端服務器是否正在運行（端口 3000）';
        } else if (err.message && err.message.includes('Proxy error')) {
          error.value = '代理連接失敗，請重啟前端開發服務器';
        } else {
          error.value = err.message || '獲取批次發放點數列表失敗';
        }
        
        // 如果是代理錯誤，不顯示錯誤提示，只顯示錯誤訊息
        if (!error.value.includes('代理') && !error.value.includes('連接')) {
          await showError('載入失敗', error.value);
        }
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
      searchTimeout.value = setTimeout(() => {
        if (pagination.value) {
          pagination.value.page = 1;
        }
        fetchBatchIssuances();
      }, 500);
    };

    const handleFilterChange = () => {
      if (pagination.value) {
        pagination.value.page = 1;
      }
      fetchBatchIssuances();
    };

    const changePage = (page) => {
      if (pagination.value) {
        pagination.value.page = page;
      }
      fetchBatchIssuances();
    };

    const handleView = (batchId) => {
      router.push(`/client/points/batch-issue/${batchId}`);
    };

    const getStatusText = (status) => {
      const statusMap = {
        processing: '執行中',
        completed: '已完成',
        failed: '失敗',
        cancelled: '已取消'
      };
      return statusMap[status] || status;
    };

    const getStatusBadgeClass = (status) => {
      const classMap = {
        processing: 'badge badge-info',
        completed: 'badge badge-success',
        failed: 'badge badge-danger',
        cancelled: 'badge badge-secondary'
      };
      return classMap[status] || 'badge badge-secondary';
    };

    const formatDateTime = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const handleGoToBatchIssue = () => {
      router.push('/client/points/batch-issue/create');
    };

    onMounted(async () => {
      await loadClientMemberSystem();
      fetchBatchIssuances();
    });

    return {
      pageTitle,
      batchIssuances,
      loading,
      error,
      pagination,
      searchQuery,
      filterStatus,
      fetchBatchIssuances,
      handleSearch,
      handleFilterChange,
      changePage,
      handleView,
      handleGoToBatchIssue,
      getStatusText,
      getStatusBadgeClass,
      formatDateTime,
      isWixtarClient
    };
  }
};
</script>

<style scoped>
.wixtar-banner {
  margin: 0 0 16px;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
}

@import '~@/assets/scss/components/_list-page-common.scss';

.page-toolbar {
  flex-wrap: wrap;
}

.search-box {
  position: relative;
  flex: 1;
  min-width: 200px;
}

.search-box input {
  padding-right: 40px;
}

.search-box i {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  pointer-events: none;
}

.filter-group {
  display: flex;
  gap: 12px;
}

.filter-group select {
  min-width: 150px;
}

/* 覆寫表格最小寬度（9 個欄位需要更大的寬度） */
.table-wrapper table {
  min-width: 900px;
}

thead {
  background: #f8f9fa;
}

th {
  padding: 12px;
  text-align: left;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #e9ecef;
}

td {
  padding: 12px;
  border-bottom: 1px solid #e9ecef;
}

tbody tr:hover {
  background: #f8f9fa;
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: #999;
}

.code-badge {
  background: #f0f0f0;
  padding: 4px 8px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 12px;
}

.progress-bar {
  position: relative;
  width: 100px;
  height: 20px;
  background: #e9ecef;
  border-radius: 10px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #28a745;
  transition: width 0.3s;
}

.progress-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 11px;
  font-weight: 600;
  color: #333;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 20px;
}

.pagination-info {
  color: #666;
  font-size: 14px;
}
</style>
